import { CodeYellowTable } from "../../components/CodeYellowTable/CodeYellowTable";
import { useGetMyGroups } from "../../api/GroupApi";
import UserHasNoGroup from "../../components/UserHasNoGroups/UserHasNoGroup";
import { Box, Button, Flex, Spinner, useDisclosure, useToast } from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import { useState, useCallback, useRef } from "react";
import { WorklistType } from "../../types/Worklist";
import { CYModal } from "../../components/CYModal/CYModal";
import { useActivateCodeYellow } from "../../api/CodeYellowApi";
import { useGetUserWorklists } from "../../api/WorklistApi";
import GroupsListModal from "../../components/GroupsListModal/GroupsListModal";
import { Limits } from "../../types/CodeYellow";
import { MyGroupResponse } from "../../types/GroupResponse";
// import LnQManager from "../../components/LnQManager/LnQManager";

const HomePage = (): JSX.Element => {
  const { data: myGroupsData, isLoading } = useGetMyGroups(1, 0);
  const { data: userWorklist } = useGetUserWorklists();
  const { mutate: activateCodeYellow } = useActivateCodeYellow();
  const toast = useToast();

  // Modal states
  const {
    isOpen: isCYModalOpen,
    onOpen: onCYModalOpen,
    onClose: onCYModalClose,
  } = useDisclosure();
  const {
    isOpen: isGroupsModalOpen,
    onOpen: onGroupsModalOpen,
    onClose: onGroupsModalClose,
  } = useDisclosure();

  // State for selection
  const [lnqType, setLnqType] = useState<WorklistType>(WorklistType.GROUP);
  const [selectedGroup, setSelectedGroup] = useState<MyGroupResponse | null>(null);
  // Track whether we're skipping the group selection
  const [skipGroupSelection, setSkipGroupSelection] = useState(false);

  // Use a ref to track if we've just selected the Personal type to prevent automatic reset
  const hasSelectedPersonal = useRef<boolean>(false);

  // Use a ref to prevent duplicated modal openings
  const isModalTransitioning = useRef<boolean>(false);

  // Track loading state during LnQ activation
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset state when modal closes
  const handleCYModalClose = useCallback(() => {
    onCYModalClose();

    // Important: Don't reset these values if we're transitioning
    // to prevent losing "personal" state between modals
    if (!isModalTransitioning.current) {
      setTimeout(() => {
        // Reset state to ensure next time we start fresh, but only if we're not transitioning
        setSelectedGroup(null);
        setSkipGroupSelection(false);
        hasSelectedPersonal.current = false;
      }, 100);
    }

    // Always reset the transition flag to allow clicks
    isModalTransitioning.current = false;
  }, [onCYModalClose]);

  // Reset state when group modal closes
  const handleGroupModalClose = useCallback(() => {
    onGroupsModalClose();
    // If we're canceling group selection, also reset selected group
    setSelectedGroup(null);
    isModalTransitioning.current = false;
  }, [onGroupsModalClose]);

  if (isLoading) {
    return <Spinner size="lg" />;
  }

  const hasGroups = (myGroupsData?.docs?.length ?? 0) > 0;

  const handleCreateLnQClick = () => {
    if (isModalTransitioning.current) {
      return; // Prevent multiple clicks during transition
    }

    isModalTransitioning.current = true;

    // If user has no groups, directly start with a personal LnQ and skip the type selection
    if (!hasGroups) {
      toast({
        title: "Group Required",
        description: "You need to create or join a group before creating an LnQ.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      isModalTransitioning.current = false;
      return;
    }

    // Normal flow for users with groups
    // Make sure we reset any previously selected group
    setSelectedGroup(null);
    setLnqType(WorklistType.GROUP);
    setSkipGroupSelection(false);
    hasSelectedPersonal.current = false;
    onCYModalOpen();
    setTimeout(() => {
      isModalTransitioning.current = false;
    }, 100);
  };

  const handleLnQTypeSelected = (type: WorklistType) => {
    if (isModalTransitioning.current) {
      return; // Prevent multiple transitions
    }

    isModalTransitioning.current = true;
    setLnqType(type);

    // For both Personal and Group, we need to select a group
    // The difference is how we process the LnQ afterwards
    hasSelectedPersonal.current = type === WorklistType.PERSONAL;
    onCYModalClose();

    // Small delay to ensure state updates properly
    setTimeout(() => {
      onGroupsModalOpen();
      isModalTransitioning.current = false;
    }, 50);
  };

  const handleGroupSelected = (group: MyGroupResponse) => {
    if (isModalTransitioning.current) {
      return; // Prevent multiple transitions
    }

    isModalTransitioning.current = true;
    setSelectedGroup(group);

    // Don't change the type here - keep the type the user selected before (Personal or Group)
    // setLnqType(WorklistType.GROUP); <- This was forcing the change to GROUP always

    onGroupsModalClose();

    // Give a short delay before opening the next modal
    setTimeout(() => {
      onCYModalOpen();
      isModalTransitioning.current = false;
    }, 200);
  };

  const handleActivateLnQ = (
    distributionType: "open" | "target",
    rvu: number,
    userIDs?: string[],
    groupId?: string,
    dateRange?: { start: string; end?: string },
    limits?: Limits,
  ) => {
    if (isModalTransitioning.current) {
      return; // Prevent multiple activations during transitions
    }

    isModalTransitioning.current = true;
    setIsSubmitting(true); // Start the loading spinner

    // Determine which group ID to use - either the passed parameter or the selected group
    // This ensures we use the correct parameter passed from CYModal
    const groupIdToUse = groupId ?? selectedGroup?.id;

    if (lnqType === WorklistType.PERSONAL && userWorklist) {
      // For personal LnQ, use the user's worklistId but include the groupId if available
      activateCodeYellow(
        {
          worklistId: userWorklist.id,
          distributionType,
          usdPerRvu: rvu,
          dateRange,
          userIds: userIDs,
          // Use the groupId parameter from the function
          groupId: groupIdToUse,
          limits,
        },
        {
          onSuccess: () => {
            toast({
              title: "Personal LnQ activated",
              description: selectedGroup
                ? `Associated with group: ${selectedGroup.facilityName}`
                : undefined,
              status: "success",
              duration: 5000,
              isClosable: true,
            });
            // Reset state after successful activation
            setSelectedGroup(null);
            setSkipGroupSelection(false);
            hasSelectedPersonal.current = false;
            isModalTransitioning.current = false;
            setIsSubmitting(false); // Stop the loading spinner
          },
          onError: () => {
            isModalTransitioning.current = false;
            setIsSubmitting(false); // Stop the loading spinner on error too
          },
        },
      );
    } else if (lnqType === WorklistType.GROUP && selectedGroup) {
      // For group LnQ, use the group's worklistId
      const worklistId = selectedGroup.worklistId;
      if (worklistId) {
        activateCodeYellow(
          {
            worklistId,
            distributionType,
            usdPerRvu: rvu,
            dateRange,
            userIds: userIDs,
            // Use the groupId parameter from the function
            groupId: groupIdToUse,
            limits,
          },
          {
            onSuccess: () => {
              toast({
                title: "Group LnQ activated",
                description: `Group: ${selectedGroup.facilityName}`,
                status: "success",
                duration: 5000,
                isClosable: true,
              });
              // Reset state after successful activation
              setSelectedGroup(null);
              setSkipGroupSelection(false);
              hasSelectedPersonal.current = false;
              isModalTransitioning.current = false;
              setIsSubmitting(false); // Stop the loading spinner
            },
            onError: () => {
              isModalTransitioning.current = false;
              setIsSubmitting(false); // Stop the loading spinner on error too
            },
          },
        );
      } else {
        isModalTransitioning.current = false;
        setIsSubmitting(false); // Stop the loading spinner
      }
    } else {
      isModalTransitioning.current = false;
      setIsSubmitting(false); // Stop the loading spinner
    }
  };

  return (
    <Box>
      {/* <LnQManager /> */}
      <Flex justifyContent="flex-end" mt={4} mb={2} mr={4}>
        <Button
          leftIcon={isSubmitting ? <Spinner size="sm" /> : <AddIcon />}
          colorScheme="yellow"
          variant="solid"
          onClick={handleCreateLnQClick}
          isDisabled={isModalTransitioning.current ?? isSubmitting}
          isLoading={isSubmitting}
        >
          Create LnQ
        </Button>
      </Flex>

      {hasGroups ? (
        <>
          <CodeYellowTable />
          {/* <MyAdminGroupsTable /> */}
        </>
      ) : (
        <UserHasNoGroup />
      )}

      {/* Modal for selecting LnQ type and completing configuration */}
      <CYModal
        isOpen={isCYModalOpen}
        onClose={handleCYModalClose}
        showLnQTypeSelection={
          hasGroups && !selectedGroup && !skipGroupSelection && !hasSelectedPersonal.current
        }
        onLnQTypeSelected={handleLnQTypeSelected}
        activate={handleActivateLnQ}
        type={getLnQType()}
        groupId={selectedGroup?.id}
        groupName={selectedGroup?.facilityName}
      />

      {/* Modal for selecting group */}
      <GroupsListModal
        isOpen={isGroupsModalOpen}
        onClose={handleGroupModalClose}
        onConfirm={handleGroupSelected}
        lnqType={lnqType}
      />
    </Box>
  );

  // Helper function to determine the LnQ type based on state
  function getLnQType(): WorklistType {
    if (hasSelectedPersonal.current) {
      return WorklistType.PERSONAL; // If user chose Personal, keep that type
    }

    if (selectedGroup) {
      return WorklistType.GROUP; // If there's a group selected, it's Group type
    }

    return lnqType; // Otherwise use the state type
  }
};

export default HomePage;
